import React, { useEffect, useMemo, useState, lazy, createElement } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, LockIcon, CheckCircleIcon, BrainIcon, AwardIcon, LaptopIcon, DollarSignIcon, GraduationCapIcon } from 'lucide-react';
import { getUniversityBySlug, getProgramBySlug } from '../data/universityData';
import { useMetaTags } from '../hooks/useMetaTags';
import { getPublisherParams } from '../utils/publisherParams';
import { trackFormSubmit } from '../utils/trackFormSubmit';
import { getMetaCookies } from '../utils/metaCookies';
// Form value → API value mappings
const educationToApiValue = (v: string): number => {
  const map: Record<string, number> = {
    'high-school': 1,
    'some-high-school': 2,
    ged: 3,
    'some-college': 5,
    associates: 5,
    bachelors: 7,
    masters: 7,
    doctorate: 7
  };
  return map[v] || 9;
};
const startTimeframeToApiValue = (v: string): number => {
  const map: Record<string, number> = {
    immediately: 1,
    '1-3-months': 2,
    '4-6-months': 3,
    '3-6-months': 3,
    '6-12-months': 4,
    '1-year-plus': 4,
    undecided: 6
  };
  return map[v] || 6;
};
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
};
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/mobile/i.test(ua)) return 'mobile';
  if (/tablet|ipad/i.test(ua)) return 'tablet';
  return 'desktop';
};
export function AdvertiserTemplatePage() {
  const {
    collegeSlug,
    programSlug
  } = useParams<{
    collegeSlug: string;
    programSlug: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const university = collegeSlug ? getUniversityBySlug(collegeSlug) : undefined;
  const initialProgram = university && programSlug ? getProgramBySlug(university, programSlug) : null;
  // Capture optional tracking params from the URL once at mount.
  // sub1/sub2/sub3 + the rest of the documented optional fields can all
  // be passed in via query string and will be forwarded on submit.
  const trackingParams = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return {
      sub1: q.get('sub1') || '',
      sub2: q.get('sub2') || '',
      sub3: q.get('sub3') || '',
      userIP: q.get('userIP') || '',
      webInitiatingURL: q.get('webInitiatingURL') || document.referrer || '',
      webLandingURL: q.get('webLandingURL') || window.location.href,
      inventoryType: q.get('inventoryType') || '',
      mediaType: q.get('mediaType') || '1'
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Inject LeadID + TrustedForm scripts AFTER first paint so they don't
  // compete with the initial render. They still load early enough to
  // observe user form interaction and produce a valid certificate.
  useEffect(() => {
    let leadIdScript: HTMLScriptElement | null = null;
    let tfScript: HTMLScriptElement | null = null;
    const inject = () => {
      ;
      (window as any).myCallbackFunction = function (token: string) {
        const field = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
        if (field) field.setAttribute('value', token);
      };
      leadIdScript = document.createElement('script');
      leadIdScript.id = 'LeadiDscript_campaign';
      leadIdScript.type = 'text/javascript';
      leadIdScript.async = true;
      leadIdScript.src = '//create.lidstatic.com/campaign/d50a3557-9854-7b8f-792c-527750401f5f.js?snippet_version=2&callback=myCallbackFunction';
      document.body.appendChild(leadIdScript);
      tfScript = document.createElement('script');
      tfScript.type = 'text/javascript';
      tfScript.async = true;
      tfScript.src = 'http' + ('https:' === document.location.protocol ? 's' : '') + '://api.trustedform.com/trustedform.js?provide_referrer=' + escape('false') + '&field=' + escape('xxTrustedFormCertUrl') + '&l=' + new Date().getTime() + Math.random();
      document.body.appendChild(tfScript);
    };
    const ric = (window as any).requestIdleCallback as ((cb: () => void, opts?: {
      timeout: number;
    }) => number) | undefined;
    const handle = ric ? ric(inject, {
      timeout: 2000
    }) : window.setTimeout(inject, 1200) as unknown as number;
    return () => {
      const cancelIdle = (window as any).cancelIdleCallback as ((h: number) => void) | undefined;
      if (ric && cancelIdle) cancelIdle(handle);else window.clearTimeout(handle);
      try {
        leadIdScript?.parentNode?.removeChild(leadIdScript);
      } catch {}
      try {
        tfScript?.parentNode?.removeChild(tfScript);
      } catch {}
    };
  }, []);
  const metaDescription = university ? initialProgram ? `Explore the ${initialProgram.degreeLevel ? `${initialProgram.degreeLevel} ` : ''}${initialProgram.name} program at ${university.name} and request information about your next steps.` : `Explore online degree programs at ${university.name} and request information about options that align with your educational goals.` : 'The requested university program could not be found. Explore accredited online degree programs on FindOnlineDegrees.';
  useMetaTags({
    title: university ? `${university.name} | Request Information` : 'Program Not Found',
    description: metaDescription,
    ogImage: university?.landingContent?.communityImage?.url,
    ogType: 'website',
    // Partner advertiser pages are paid lead-gen surfaces; keep them out
    // of the index so they don't compete with the brand site for SEO.
    robots: 'noindex, follow'
  });
  // Campus-aware advertisers (e.g. SJVC) use a step 0 to look up the user's
  // campus by zip before showing program options.
  const hasCampusFlow = Boolean(university?.campusZipLookup);
  const firstStep = hasCampusFlow ? 0 : 1;
  const totalSteps = hasCampusFlow ? 5 : 4;
  const [step, setStep] = useState(firstStep);
  const [formData, setFormData] = useState({
    degreeLevel: initialProgram?.degreeLevel || '',
    areaOfStudy: initialProgram?.category || '',
    program: initialProgram?.name || '',
    highestEducation: '',
    startDate: '',
    hsGradYear: '',
    zipCode: '',
    city: '',
    state: '',
    streetAddress: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consent: false,
    consentSms: false,
    campusId: 0,
    campusName: ''
  });
  const [error, setError] = useState('');
  const [campusLookupError, setCampusLookupError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // Auto-advance Step 2
  useEffect(() => {
    if (step === 2 && formData.highestEducation && formData.startDate && formData.hsGradYear) {
      const timer = setTimeout(() => {
        setError('');
        setStep(3);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.highestEducation, formData.startDate, formData.hsGradYear, step]);
  // ZIP Code Lookup
  useEffect(() => {
    if (formData.zipCode.length === 5) {
      fetch(`https://api.zippopotam.us/us/${formData.zipCode}`).then(res => {
        if (res.ok) return res.json();
        throw new Error('Invalid zip');
      }).then(data => {
        if (data.places && data.places.length > 0) {
          setFormData(prev => ({
            ...prev,
            city: data.places[0]['place name'],
            state: data.places[0]['state abbreviation']
          }));
        }
      }).catch(() => {
        // Handle error silently
      });
    }
  }, [formData.zipCode]);
  if (!university) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-634" data-mp="2cbi33vqetol1">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center" data-id="element-635" data-mp="27c5fh8rpsdhe">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-636" data-mp="22dawa5pu4mwr">
            Program Not Found
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-637" data-mp="1xehlil1xna0a">
            We couldn't find the specific program or university you're looking
            for.
          </p>
          <button onClick={() => navigate('/')} className="w-full py-3 bg-gray-900 text-white rounded-lg font-bold hover:bg-gray-800 transition-colors" data-id="element-638" data-mp="1sl4vc6hluv5f">
            Return Home
          </button>
        </div>
      </div>;
  }
  const updateField = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };
  const handleContinue = () => {
    if (step === 0) {
      if (!formData.zipCode || formData.zipCode.length !== 5) {
        setCampusLookupError('Please enter your 5-digit ZIP code to continue.');
        return;
      }
      if (!formData.campusId) {
        setCampusLookupError(`Sorry, that ZIP code isn't served by a ${university.shortName} campus. Please check your ZIP and try again.`);
        return;
      }
      const campusPrograms = university.programsForCampus ? university.programsForCampus(formData.campusId) : university.programs;
      const preselectStillValid = !!initialProgram && campusPrograms.some(p => p.name === initialProgram.name);
      setFormData(prev => ({
        ...prev,
        degreeLevel: preselectStillValid ? initialProgram!.degreeLevel || '' : '',
        areaOfStudy: preselectStillValid ? initialProgram!.category : '',
        program: preselectStillValid ? initialProgram!.name : ''
      }));
      setCampusLookupError('');
      setStep(1);
      return;
    }
    if (step === 1) {
      if (university.singleProgramSelect) {
        if (!formData.program) {
          setError('Please select a program to continue.');
          return;
        }
      } else if (!formData.degreeLevel || !formData.areaOfStudy || !formData.program) {
        setError('Please complete all fields to continue.');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!formData.highestEducation || !formData.startDate || !formData.hsGradYear) {
        setError('Please complete all fields to continue.');
        return;
      }
      setStep(3);
    } else if (step === 3) {
      if (!formData.city || !formData.state) {
        setError('Please enter a valid ZIP code.');
        return;
      }
      if (!formData.streetAddress.trim()) {
        setError('Please enter your street address.');
        return;
      }
      setStep(4);
    }
  };
  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.startsWith('1')) return formData.phone;
    const limited = digits.slice(0, 10);
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
  };
  const handleSubmit = () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (formData.phone.replace(/\D/g, '').length !== 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (!university.consentOnSubmit && !formData.consent) {
      setError('Please agree to the consent terms.');
      return;
    }
    if (university.tcpaTextSms && !formData.consentSms) {
      setError('Please check both consent boxes to continue.');
      return;
    }
    setIsSubmitting(true);
    // Read tracking values populated by the LeadID + TrustedForm scripts
    const leadIdInput = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
    const tfInput = document.getElementsByName('xxTrustedFormCertUrl')[0] as HTMLInputElement | undefined;
    const leadIdToken = leadIdInput?.value || generateUUID();
    const trustedFormUrl = tfInput?.value || '';
    // Build the full lead payload per advertiser parameter spec.
    // Includes: required advertiser identifiers, mapped form values,
    // PII, and optional tracking parameters.
    const payload: Record<string, string> = {
      // Required advertiser identifiers. URL params (mPubGroupID, or
      // mPubID + mPubCampaignID) override the per-university config values
      // when present; otherwise fall back to the university config.
      ...getPublisherParams(location.search, {
        mPubID: university.mPubID || '',
        mPubCampaignID: university.mPubCampaignID || ''
      }),
      mBrandID: university.mBrandID || '',
      hpRequestId: generateUUID(),
      mSessionID: `s-${generateUUID()}`,
      leadIdToken,
      // Mapped form values. When the partner provides a numeric program
      // catalog (programId), post that ID as mProgram; otherwise fall back
      // to the program name.
      degreeInterest: formData.degreeLevel,
      areaStudy: formData.areaOfStudy,
      mProgram: (() => {
        const matched = programsForThisFlow.find(p => p.name === formData.program);
        if (matched?.mProgramValue) return matched.mProgramValue;
        return matched?.programId ? String(matched.programId) : formData.program;
      })(),
      highestEducation: String(educationToApiValue(formData.highestEducation)),
      startTimeframe: String(startTimeframeToApiValue(formData.startDate)),
      gradYear: formData.hsGradYear,
      // PII
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone.replace(/\D/g, ''),
      address: formData.streetAddress,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
      campusLocation: formData.campusId ? String(formData.campusId) : '',
      smsConsent: formData.consentSms ? '1' : '0',
      // Optional tracking
      useragent: navigator.userAgent,
      sub1: trackingParams.sub1,
      sub2: trackingParams.sub2,
      sub3: trackingParams.sub3,
      userIP: trackingParams.userIP,
      webInitiatingURL: trackingParams.webInitiatingURL,
      webLandingURL: trackingParams.webLandingURL,
      trustedFormUrl,
      inventoryType: trackingParams.inventoryType,
      deviceType: getDeviceType(),
      mediaType: trackingParams.mediaType
    };
    // Meta match keys for server-side (CAPI) attribution.
    const {
      fbp,
      fbc
    } = getMetaCookies();
    if (fbp) payload.fbp = fbp;
    if (fbc) payload.fbc = fbc;
    console.log('[Advertiser Lead] payload:', payload);
    // Report the submission to GTM once (and only once) the lead has been
    // sent. `leadIdToken` doubles as the dedupe id for Meta CAPI.
    let reported = false;
    const reportSubmission = () => {
      if (reported) return;
      reported = true;
      trackFormSubmit({
        formId: 'university-request-info',
        pageUrl: window.location.pathname,
        leadId: leadIdToken
      });
    };
    // Host-and-post: the advertiser provides a posting endpoint per partner.
    // Configure it via university.leadPostUrl. If unset, no post is fired
    // (the payload is still logged above for QA / verification).
    const postBase = university.leadPostUrl || '';
    if (postBase) {
      const qs = new URLSearchParams(payload).toString();
      const fullUrl = `${postBase}?${qs}`;
      console.log('[Advertiser Lead] POST URL:', fullUrl);
      try {
        const pixel = new Image();
        pixel.onload = reportSubmission;
        pixel.src = fullUrl;
      } catch (err) {
        console.error('[Advertiser Lead] post error:', err);
      }
    } else {
      console.log('[Advertiser Lead] No leadPostUrl configured for this advertiser — skipping post.');
    }
    // Meta Pixel: CompleteRegistration — the standard conversion event used
    // for every completed lead form on the site. `eventID` matches the
    // dataLayer `event_id` so browser and server (CAPI) events dedupe.
    if (window.fbq) {
      window.fbq('track', 'CompleteRegistration', {
        content_name: formData.program,
        content_category: formData.areaOfStudy
      }, {
        eventID: leadIdToken
      });
    }
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      // Advertisers without a configured posting endpoint never produce a
      // pixel load, so confirm on the success state instead.
      reportSubmission();
      setTimeout(() => {
        navigate('/');
      }, 3000);
    }, 1000);
  };
  // Derived program list — honors campus-aware advertisers (filters to the
  // programs available at the user's matched campus).
  const programsForThisFlow = hasCampusFlow && formData.campusId && university.programsForCampus ? university.programsForCampus(formData.campusId) : university.programs;
  // Derived options for Step 1
  const uniqueAreas = Array.from(new Set(programsForThisFlow.filter(p => !formData.degreeLevel || p.degreeLevel === formData.degreeLevel).map(p => p.category)));
  const availablePrograms = programsForThisFlow.filter(p => (!formData.degreeLevel || p.degreeLevel === formData.degreeLevel) && (!formData.areaOfStudy || p.category === formData.areaOfStudy));
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: 60
  }, (_, i) => currentYear - i);
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.5rem center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '1.5em 1.5em',
    paddingRight: '2.5rem'
  };
  if (isSuccess) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-639" data-mp="1g8fze6v2zey4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 max-w-md w-full text-center" data-id="element-640" data-mp="1l5oic9o1zn0f">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-641" data-mp="1p94d10gkbdah">
            <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-642" data-mp="2wld9ld8cb871" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-643" data-mp="30m6udxqhk6vp">
            Thanks, {formData.firstName}!
          </h2>
          <p className="text-gray-600" data-id="element-644" data-mp="ozd1c8f5ph92">
            {university.name} will reach out shortly. Redirecting you to the
            homepage...
          </p>
        </div>
      </div>;
  }
  // Dynamic hero title — show the selected program once chosen, otherwise a generic prompt.
  // On the first step (entry view), prefer the partner's high-CTR landing headline if provided.
  const isEntryStep = step === firstStep;
  // Once the user has chosen both a program and a degree level, surface the
  // degree in the H1 (e.g. "Bachelor's in Early Childhood Education").
  const selectedProgramTitle = formData.program && formData.degreeLevel ? `${formData.degreeLevel} in ${formData.program}` : formData.program || initialProgram?.name;
  const heroTitle = isEntryStep && university.landingContent ? university.landingContent.headline : selectedProgramTitle || `Find Your ${university.shortName} Program`;
  // Show the fuller landing subheadline on every step (entry and beyond) so
  // the program-selection / later steps aren't left with just the short
  // mission line. Falls back to the mission when no landing copy exists.
  const heroDescription = university.landingContent?.subheadline !== undefined ? university.landingContent.subheadline : university.mission;
  const featureIconMap: Record<string, any> = {
    BrainIcon,
    AwardIcon,
    LaptopIcon,
    DollarSignIcon,
    GraduationCapIcon
  };
  return <div className="min-h-screen font-sans flex flex-col bg-gray-50" data-id="element-645" data-mp="1s7u8icwfj1pu">
      {/* Hidden tracking fields populated by LeadID + TrustedForm scripts */}
      <input type="hidden" name="leadid_token" value="" data-id="element-646" data-mp="1nbnd3e1nt8m5" />
      <input type="hidden" name="xxTrustedFormCertUrl" value="" data-id="element-647" data-mp="1ic0zh3hx5qct" />

      {/* White top nav bar with centered logo */}
      <header className="w-full bg-white border-b border-gray-100 py-5 px-4" data-id="element-648" data-mp="3hnblexddycxk">
        <div className="max-w-7xl mx-auto flex items-center justify-center relative" data-id="element-649" data-mp="3cps2vl9nndzt">
          {university.logoUrl ? <img src={university.logoUrl} alt={university.name} decoding="async" className="h-12 md:h-14 object-contain" data-id="element-650" data-mp="3k7zrgibcfbty" /> : <span className="text-xl font-bold" style={{
          color: university.primaryColor
        }} data-id="element-651" data-mp="3u05ucn34vdnx">
              {university.name}
            </span>}
          <div className="hidden md:flex items-center gap-1.5 text-gray-400 text-xs font-medium absolute right-0" data-id="element-652" data-mp="368i6ke164ipv">
            <LockIcon className="w-3.5 h-3.5" data-id="element-653" data-mp="3b71w0oez0cmz" />
            Secure
          </div>
        </div>
      </header>

      {/* Branded main wrapper — neutral surface for the supporting sections */}
      <main className="flex-grow bg-white" data-id="element-654" data-mp="qhm8c2b1t41k">
        {/* HERO — full-bleed brand color, optionally overlaid with a partner image */}
        <section className="relative py-12 md:py-20 px-4 bg-cover bg-center" style={{
        backgroundColor: university.primaryColor,
        backgroundImage: university.heroBackgroundImage ? `url(${university.heroBackgroundImage})` : undefined
      }} data-id="element-655" data-mp="vf4an9uk8l0l">
          {/* Mobile-only eyebrow + H1 — rendered ABOVE the grid so the form
           can sit directly beneath it and stay above the fold on phones.
           Desktop renders the eyebrow + H1 inside the left column instead. */}
          <div className="relative max-w-6xl mx-auto lg:hidden text-white mb-6" data-id="element-656" data-mp="23u9axogg58vi">
            <p className="text-sm font-semibold mb-3 text-white/90" data-id="element-657" data-mp="23s6cqh5549fp">
              {university.name}
            </p>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-[1.05] tracking-tight" data-id="element-658" data-mp="2cuxqwopehjb1">
              {heroTitle}
            </h1>
          </div>

          <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start" data-id="element-659" data-mp="1p3jxsgjfdjp0">
            {/* Left: Branded hero copy — appears BELOW the form on mobile,
             beside it on desktop. */}
            <div className="text-white lg:pt-8 order-2 lg:order-1" data-id="element-660" data-mp="1axqefjqbsbhg">
              {/* Desktop-only eyebrow + H1 (mobile renders these above the grid). */}
              <p className="hidden lg:block text-base font-semibold mb-4 text-white/90" data-id="element-661" data-mp="112pyi8hupr43">
                {university.name}
              </p>
              <h1 className="hidden lg:block text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight mb-6" data-id="element-662" data-mp="15y5fh9b5o34q">
                {heroTitle}
              </h1>
              {heroDescription && <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-lg" data-id="element-663" data-mp="1js4zk2xjeg8h">
                  {heroDescription}
                </p>}
              {university.landingContent?.heroHighlights && university.landingContent.heroHighlights.length > 0 && <ul className="mt-6 space-y-2.5 max-w-lg" data-id="element-664" data-mp="dbknq7qrpp0n">
                    {university.landingContent.heroHighlights.map(h => <li key={h} className="flex items-start gap-2.5 text-sm md:text-base text-white/90" data-id="element-665" data-mp="tjqr494dp67f">
                        <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-white/70" data-id="element-666" data-mp="jsco3rd7xo9j" />
                        <span data-id="element-667" data-mp="euuc1s3wmsxb">{h}</span>
                      </li>)}
                  </ul>}
            </div>

            {/* Right: Form card — appears directly under the H1 on mobile
             (above the fold), to the right on desktop. */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 md:p-8 lg:p-10 order-1 lg:order-2" data-id="element-668" data-mp="12jdie23e8vmr">
              {/* Progress Indicator */}
              <div className="mb-6" data-id="element-669" data-mp="xkalj4xkhvpv">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4" data-id="element-670" data-mp="3n9mia4p1nlxp">
                  {university.formHeading || 'Take the next step in your education.'}
                </h2>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-671" data-mp="1xsqyibkdxa71">
                  <div className="h-full transition-all duration-300" style={{
                  width: `${(step - firstStep + 1) / totalSteps * 100}%`,
                  backgroundColor: university.ctaColor
                }} data-id="element-672" data-mp="1xqw9al22yvx5" />
                </div>
              </div>

              {step > firstStep && <button onClick={() => {
              setStep(step - 1);
              setError('');
            }} className="flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-6 transition-colors" data-id="element-673" data-mp="2hgggjr2irw32">
                  <ArrowLeftIcon className="w-4 h-4" data-id="element-674" data-mp="2vf6f7b4zm3tj" />
                  <span className="text-sm font-medium" data-id="element-675" data-mp="w3n3iosauhg6">Back</span>
                </button>}

              {/* Step 0: Campus lookup by zip (campus-aware advertisers only) */}
              {step === 0 && hasCampusFlow && <div className="space-y-6" data-id="element-676" data-mp="t2jxqrzjpbuo">
                  <p className="text-sm text-gray-600 text-center -mt-2" data-id="element-677" data-mp="t3ljpri3nir6">
                    Enter your ZIP code to see programs available at your{' '}
                    {university.shortName} campus.
                  </p>

                  <div data-id="element-678" data-mp="y0c0yi5n4ke6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-679" data-mp="y03oushcdj41">
                      ZIP Code
                    </label>
                    <input type="text" inputMode="numeric" value={formData.zipCode} onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                  setCampusLookupError('');
                  // Auto-resolve campus when the zip becomes valid.
                  // If no match, leave the dropdown alone so the
                  // user can still pick manually.
                  if (val.length === 5 && university.campusZipLookup) {
                    const match = university.campusZipLookup(val);
                    if (match) {
                      setFormData(prev => ({
                        ...prev,
                        zipCode: val,
                        campusId: match.id,
                        campusName: match.name
                      }));
                      return;
                    }
                  }
                  updateField('zipCode', val);
                }} placeholder="e.g. 93291" maxLength={5} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-lg" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-680" data-mp="1bya5kvu5t2gh" />
                  </div>

                  <div data-id="element-681" data-mp="1o4rts58sygma">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-682" data-mp="1o25pceh7kh8a">
                      Campus
                    </label>
                    <select value={formData.campusId || ''} disabled aria-readonly="true" tabIndex={-1} className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg text-gray-700 appearance-none cursor-not-allowed" style={selectStyle as any} data-id="element-683" data-mp="1e4r7qbjjtmee">
                      <option value="" data-id="element-684" data-mp="vgm73cy6uhjt">
                        -- Enter ZIP to match a campus --
                      </option>
                      {(university.landingContent?.campusList || []).map(c => <option key={c.id} value={c.id} data-id="element-685" data-mp="ljhkxd4cem30">
                            {c.name} Campus
                          </option>)}
                    </select>
                    <p className="mt-2 text-xs text-gray-500" data-id="element-686" data-mp="lbnor57gdpzn">
                      Your campus is automatically determined by your ZIP code.
                    </p>
                  </div>

                  {campusLookupError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-687" data-mp="3ot5v187kuoxy">
                      <p className="text-red-600 text-sm" data-id="element-688" data-mp="3tqeh3jvujd4h">
                        {campusLookupError}
                      </p>
                    </div>}
                </div>}

              {/* Step 1: Program Selection — single dropdown variant */}
              {step === 1 && university.singleProgramSelect && <div className="space-y-6" data-id="element-689" data-mp="35zir8sayarzv">
                  <div data-id="element-690" data-mp="3azwqbkukqrlr">
                    <label htmlFor="program-select" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-691" data-mp="3azev8rjnejff">
                      Program of Interest
                    </label>
                    <select id="program-select" value={formData.program} onChange={e => {
                  const selected = programsForThisFlow.find(p => p.name === e.target.value);
                  updateField('program', e.target.value);
                  updateField('degreeLevel', selected?.degreeLevel || '');
                  updateField('areaOfStudy', selected?.category || '');
                }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-692" data-mp="29c4mlcvq53iz">
                      <option value="" data-id="element-693" data-mp="1bywj7gba32zd">-- Select Program --</option>
                      {programsForThisFlow.map(p => <option key={p.name} value={p.name} data-id="element-694" data-mp="16zjyp27vq7zy">
                          {p.name}
                        </option>)}
                    </select>
                  </div>
                </div>}

              {/* Step 1: Program Selection */}
              {step === 1 && !university.singleProgramSelect && <div className="space-y-6" data-id="element-695" data-mp="1ur8ni8lhsec5">
                  <div data-id="element-696" data-mp="1qnkgps4opmry">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-697" data-mp="1qoc9kv6wd6ej">
                      Degree Level
                    </label>
                    <select value={formData.degreeLevel} onChange={e => {
                  updateField('degreeLevel', e.target.value);
                  updateField('areaOfStudy', '');
                  updateField('program', '');
                }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-698" data-mp="1zoqys5mzf5nb">
                      <option value="" data-id="element-699" data-mp="1p05yv6685ufp">-- Select Degree Level --</option>
                      {university.degreeLevels.map(level => <option key={level} value={level} data-id="element-700" data-mp="3qu58zo7m25qt">
                          {level}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-701" data-mp="3o9pffu1v6a53">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-702" data-mp="3o9z5fhmwshas">
                      Area of Study
                    </label>
                    <select value={formData.areaOfStudy} onChange={e => {
                  updateField('areaOfStudy', e.target.value);
                  updateField('program', '');
                }} disabled={!formData.degreeLevel} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-703" data-mp="3ejdy95t664g9">
                      <option value="" data-id="element-704" data-mp="9kp1upr7t113">-- Select Area of Study --</option>
                      {uniqueAreas.map(area => <option key={area} value={area} data-id="element-705" data-mp="ebf9k9n45fyy">
                          {area}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-706" data-mp="4kkgalbg9g40">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-707" data-mp="4lvl8ogmstwp">
                      Program
                    </label>
                    <select value={formData.program} onChange={e => updateField('program', e.target.value)} disabled={!formData.areaOfStudy} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-708" data-mp="x9rgrpugsfrw">
                      <option value="" data-id="element-709" data-mp="3agpslz31fxh6">-- Select Program --</option>
                      {availablePrograms.map(p => <option key={p.name} value={p.name} data-id="element-710" data-mp="35hd5rec62qel">
                          {p.name}
                        </option>)}
                    </select>
                  </div>
                </div>}

              {/* Step 2: Academic Background */}
              {step === 2 && <div className="space-y-6" data-id="element-711" data-mp="2rin4dmibe4kf">
                  <div data-id="element-712" data-mp="2mr57panbqq59">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-713" data-mp="2mqvdtbz5ryua">
                      Highest Level of Education
                    </label>
                    <select value={formData.highestEducation} onChange={e => updateField('highestEducation', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-714" data-mp="1yz7upg851s2k">
                      <option value="" data-id="element-715" data-mp="24tuq89txz7mf">-- Please Select --</option>
                      <option value="high-school" data-id="element-716" data-mp="29s60ums29m5p">
                        High School Diploma/GED
                      </option>
                      <option value="some-college" data-id="element-717" data-mp="2et3h2ttig64k">Some College</option>
                      <option value="associates" data-id="element-718" data-mp="3l8dt9vds3h3n">Associate's Degree</option>
                      <option value="bachelors" data-id="element-719" data-mp="3q6fdijmxxmgp">Bachelor's Degree</option>
                      <option value="masters" data-id="element-720" data-mp="3q8s41l6rl55d">Master's Degree</option>
                      <option value="doctorate" data-id="element-721" data-mp="1ei0ttfsf3y1h">Doctorate</option>
                      <option value="some-high-school" data-id="element-722" data-mp="19jpit1qdzhho">Some High School</option>
                    </select>
                  </div>

                  <div data-id="element-723" data-mp="14s7qf22fip8b">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-724" data-mp="14rfvzukkixjm">
                      When would you like to start classes?
                    </label>
                    <select value={formData.startDate} onChange={e => updateField('startDate', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-725" data-mp="vtm4qy1nuu3b">
                      <option value="" data-id="element-726" data-mp="1k4s8ycve60yw">-- Please Select --</option>
                      <option value="immediately" data-id="element-727" data-mp="1p3lcbcgysblc">Immediately</option>
                      <option value="1-3-months" data-id="element-728" data-mp="1bbx3bi20i38l">1-3 months</option>
                      <option value="3-6-months" data-id="element-729" data-mp="1bbop9mu5krvt">3-6 months</option>
                      <option value="6-12-months" data-id="element-730" data-mp="1fcgy4ez44urk">6-12 months</option>
                      <option value="1-year-plus" data-id="element-731" data-mp="11e0tquoq3gcl">1+ year</option>
                      <option value="undecided" data-id="element-732" data-mp="11cg0r9i245gu">Undecided</option>
                    </select>
                  </div>

                  <div data-id="element-733" data-mp="16ahjftbkm2rs">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-734" data-mp="sdkq9l7zc97q">
                      High School Graduation Year
                    </label>
                    <select value={formData.hsGradYear} onChange={e => updateField('hsGradYear', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-735" data-mp="wghfvvvxt5l6">
                      <option value="" data-id="element-736" data-mp="ys9wf0pj6czr">-- Please Select --</option>
                      {years.map(year => <option key={year} value={year} data-id="element-737" data-mp="ttgij76c0jz2">
                          {year}
                        </option>)}
                      <option value="before-1965" data-id="element-738" data-mp="12uwpf4xxu3yj">Before 1965</option>
                    </select>
                  </div>
                </div>}

              {/* Step 3: Location */}
              {step === 3 && <div className="space-y-6" data-id="element-739" data-mp="34n2prytezexf">
                  {formData.city && formData.state ? <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3" data-id="element-740" data-mp="1ktv2an0zciq4">
                      <span className="text-green-600 text-xl" data-id="element-741" data-mp="1fv1xrk3o8223">📍</span>
                      <div className="flex-1" data-id="element-742" data-mp="1tu1f06outeo8">
                        <p className="text-sm font-semibold text-gray-900" data-id="element-743" data-mp="1tut5x47iao86">
                          {formData.city}, {formData.state} {formData.zipCode}
                        </p>
                      </div>
                      <button type="button" onClick={() => {
                  updateField('city', '');
                  updateField('state', '');
                  updateField('zipCode', '');
                }} className="text-sm text-green-600 hover:text-green-700 font-semibold" data-id="element-744" data-mp="23pl8ynjuy3ac">
                        change
                      </button>
                    </div> : <div data-id="element-745" data-mp="314w53bwucb2d">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-746" data-mp="314mgnsrg2bys">
                        ZIP Code
                      </label>
                      <input type="text" inputMode="numeric" value={formData.zipCode} onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                  updateField('zipCode', val);
                }} placeholder="e.g. 90210" maxLength={5} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-747" data-mp="361mmq3oanepw" />
                    </div>}

                  {formData.city && formData.state && <div className="animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-748" data-mp="563obfw0iccg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-749" data-mp="94yvanamtkl">
                        Street Address
                      </label>
                      <input type="text" value={formData.streetAddress} onChange={e => updateField('streetAddress', e.target.value)} placeholder="123 Main St" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-750" data-mp="sx0wchkospif" />
                    </div>}
                </div>}

              {/* Step 4: Contact & Submit */}
              {step === 4 && <div className="space-y-6" data-id="element-751" data-mp="1o5kvyycx307d">
                  <p className="text-sm text-gray-600 text-center -mt-2" data-id="element-752" data-mp="1o4jmgybjulrr">
                    Where should {university.shortName} send your information?
                  </p>

                  <div className="grid grid-cols-2 gap-4" data-id="element-753" data-mp="1xvwcrlhp93vo">
                    <div data-id="element-754" data-mp="22uhc7ip4njj2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-755" data-mp="22tphsb79nrud">
                        First Name
                      </label>
                      <input type="text" value={formData.firstName} onChange={e => updateField('firstName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                    '--tw-ring-color': university.primaryColor
                  } as any} data-id="element-756" data-mp="2bvp1xcdkvc1j" />
                    </div>
                    <div data-id="element-757" data-mp="3ndjfcgy582ov">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-758" data-mp="3na5cl7lkhrgw">
                        Last Name
                      </label>
                      <input type="text" value={formData.lastName} onChange={e => updateField('lastName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                    '--tw-ring-color': university.primaryColor
                  } as any} data-id="element-759" data-mp="2uom06mp8am0c" />
                    </div>
                  </div>

                  <div data-id="element-760" data-mp="hguta9r2638r">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-761" data-mp="hhcw5p887fpn">
                      Email Address
                    </label>
                    <input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-762" data-mp="mg7i301hpkdz" />
                  </div>

                  <div data-id="element-763" data-mp="3pwwlid5s0dzf">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-764" data-mp="3py7r8iis7vt6">
                      Phone Number
                    </label>
                    <input type="tel" value={formData.phone} onChange={e => updateField('phone', formatPhoneNumber(e.target.value))} placeholder="555-123-4567" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-765" data-mp="1rf4cs2z5v6nb" />
                  </div>

                  <div className="pt-4 space-y-3" data-id="element-766" data-mp="ys9v1dtqaldj">
                    {university.tcpaTextSms && <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-767" data-mp="2y16s5gl3yrz6">
                        <input type="checkbox" checked={formData.consentSms} onChange={e => updateField('consentSms', e.target.checked)} className="mt-1 w-5 h-5 border-gray-300 rounded focus:ring-2 focus:ring-offset-2" style={{
                    accentColor: university.primaryColor
                  }} data-id="element-768" data-mp="31kfgh6c0fh87" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-769" data-mp="2mprdbyu30uvr">
                          {university.tcpaTextSms}
                        </span>
                      </label>}
                    {university.consentOnSubmit ?
                // Consent is given by clicking the submit button, so
                // the disclosure is shown as plain text.
                <p className="text-xs text-gray-600 leading-relaxed p-4 rounded-lg border border-gray-200 bg-gray-50" data-id="element-770" data-mp="wzxdpcv7fvr4">
                        {university.tcpaText}
                      </p> : <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-771" data-mp="16qa9k18gzbck">
                        <input type="checkbox" checked={formData.consent} onChange={e => updateField('consent', e.target.checked)} className="mt-1 w-5 h-5 border-gray-300 rounded focus:ring-2 focus:ring-offset-2" style={{
                    accentColor: university.primaryColor
                  }} data-id="element-772" data-mp="11x7cvk8ebls5" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-773" data-mp="3mheka3qsvtbj">
                          {university.tcpaText || `By checking this box, I consent to receive calls and text messages from ${university.name} at the number provided above regarding educational opportunities. I understand that these calls may be generated using an automated technology and that my consent is not required to enroll. Message and data rates may apply.`}
                        </span>
                      </label>}
                  </div>
                </div>}

              {error && <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-774" data-mp="1o8quqd82awmm">
                  <p className="text-red-600 text-sm" data-id="element-775" data-mp="1a8zioi13vqb9">{error}</p>
                </div>}

              <div className="mt-8" data-id="element-776" data-mp="1e9rvfl96sdca">
                {step < 4 ? <button onClick={handleContinue} className="w-full py-4 font-bold rounded-lg text-lg transition-all shadow-md hover:shadow-lg" style={{
                backgroundColor: university.ctaColor,
                color: university.ctaTextColor || '#ffffff'
              }} data-id="element-777" data-mp="34lfnrfi60yej">
                    Continue
                  </button> : <button onClick={handleSubmit} disabled={isSubmitting || !university.consentOnSubmit && !formData.consent || Boolean(university.tcpaTextSms) && !formData.consentSms} className="w-full py-4 font-bold rounded-lg text-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" style={{
                backgroundColor: university.ctaColor,
                color: university.ctaTextColor || '#ffffff'
              }} data-id="element-778" data-mp="o5vy4y4kr5yo">
                    {isSubmitting ? 'Submitting...' : university.submitLabel || 'Submit Application'}
                  </button>}
              </div>
            </div>
          </div>
        </section>
        {/* /HERO */}

        {/* Supporting site content — renders on every step so the
            program page (deep link) reads as a full marketing site,
            not just a form. */}
        {university.landingContent && <>
            {/* Stats strip */}
            {university.landingContent.stats && <section className="bg-white border-b border-gray-100 py-10 md:py-14 px-4" data-id="element-779" data-mp="3k5fz78tjvfzk">
                <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8" data-id="element-780" data-mp="3g203qft7vlog">
                  {university.landingContent.stats.map(s => <div key={s.label} className="text-center" data-id="element-781" data-mp="24opc7yek20vq">
                      <div className="text-3xl md:text-4xl font-extrabold mb-1" style={{
                color: university.primaryColor
              }} data-id="element-782" data-mp="24m37s7myo1hq">
                        {s.value}
                      </div>
                      <div className="text-xs md:text-sm font-semibold uppercase tracking-wider text-gray-500" data-id="element-783" data-mp="262z0c90vq8zg">
                        {s.label}
                      </div>
                    </div>)}
                </div>
              </section>}

            {/* Why us — feature grid */}
            <section className="bg-gray-50 py-16 md:py-24 px-4" data-id="element-784" data-mp="1n67epsxv7hs8">
              <div className="max-w-6xl mx-auto" data-id="element-785" data-mp="1n8k4gs9v6wfu">
                <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-786" data-mp="18ick270ar3ci">
                  <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
                color: university.ctaColor
              }} data-id="element-787" data-mp="1dgnuojyf1hvs">
                    Why {university.shortName}
                  </p>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight" data-id="element-788" data-mp="ku67ywruj6m6">
                    Education built around your career goals.
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-789" data-mp="tuvwrgiovwqe">
                  {university.landingContent.features.map(f => {
                const Icon = featureIconMap[f.icon] || GraduationCapIcon;
                return <div key={f.title} className="bg-white rounded-xl border border-gray-200 p-6 text-center hover:shadow-md transition-shadow" data-id="element-790" data-mp="13r7ggrklzouv">
                        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{
                    backgroundColor: `${university.ctaColor}15`
                  }} data-id="element-791" data-mp="1dko75mpg8nub">
                          <Icon className="w-7 h-7" style={{
                      color: university.ctaColor
                    }} data-id="element-792" data-mp="1hilyow6ozm0v" />
                        </div>
                        <h3 className="text-sm font-bold tracking-widest uppercase text-gray-900 mb-2" data-id="element-793" data-mp="3lr0699agcwlg">
                          {f.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-794" data-mp="2t2kt6savsocr">
                          {f.description}
                        </p>
                      </div>;
              })}
                </div>
              </div>
            </section>

            {/* Info sections — alternating layout */}
            {university.landingContent.infoSections && university.landingContent.infoSections.map((s, i) => <section key={s.heading} className={`py-16 md:py-24 px-4 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-id="element-795" data-mp="hemjnj0t37de" data-mpi={i}>
                  <div className="max-w-5xl mx-auto" data-id="element-796" data-mp="wes2yqtfepis">
                    {s.eyebrow && <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
              color: university.ctaColor
            }} data-id="element-797" data-mp="rhi6ir7mddlg">
                        {s.eyebrow}
                      </p>}
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5 max-w-3xl" data-id="element-798" data-mp="dmfpp918i3tv">
                      {s.heading}
                    </h2>
                    <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6 max-w-3xl" data-id="element-799" data-mp="3r4ircw2i0wo">
                      {s.body}
                    </p>
                    {s.bullets && <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl" data-id="element-800" data-mp="1y43v8x55iezq">
                        {s.bullets.map(b => <li key={b} className="flex items-start gap-3 text-gray-700" data-id="element-801" data-mp="3nm13pxjr1bql">
                            <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                  color: university.ctaColor
                }} data-id="element-802" data-mp="3sekhauifs6ve" />
                            <span className="text-sm md:text-base" data-id="element-803" data-mp="a7e5fe33ln3k">{b}</span>
                          </li>)}
                      </ul>}
                  </div>
                </section>)}

            {/* Community image — partner-supplied lifestyle photo */}
            {university.landingContent.communityImage && <section className="bg-white py-16 md:py-24 px-4" data-id="element-804" data-mp="2lvdu0olnog02">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center" data-id="element-805" data-mp="2qvicula1dmtg">
                  <div className="order-2 md:order-1" data-id="element-806" data-mp="2idyhesk8515w">
                    {university.landingContent.communityImage.eyebrow && <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
                color: university.ctaColor
              }} data-id="element-807" data-mp="2ec4kmvd5ghb0">
                        {university.landingContent.communityImage.eyebrow}
                      </p>}
                    {university.landingContent.communityImage.heading && <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5" data-id="element-808" data-mp="29dtbkmuoiatu">
                        {university.landingContent.communityImage.heading}
                      </h2>}
                    {university.landingContent.communityImage.body && <p className="text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-809" data-mp="1byp68oblt1ky">
                        {university.landingContent.communityImage.body}
                      </p>}
                  </div>
                  <div className="order-1 md:order-2 flex justify-center" data-id="element-810" data-mp="vf8orhayodqq">
                    <img src={university.landingContent.communityImage.url} alt={university.landingContent.communityImage.alt || university.name} loading="lazy" decoding="async" className="w-full max-w-md object-contain" data-id="element-811" data-mp="10cr01ecgb51w" />
                  </div>
                </div>
              </section>}

            {/* Testimonial */}
            {university.landingContent.testimonial && <section className="py-16 md:py-24 px-4" style={{
          backgroundColor: `${university.primaryColor}08`
        }} data-id="element-812" data-mp="1t27pc34ry5tw">
                <div className="max-w-3xl mx-auto text-center" data-id="element-813" data-mp="3hlbxytjd1iql">
                  <div className="text-6xl leading-none mb-4 font-serif" style={{
              color: university.primaryColor
            }} data-id="element-814" data-mp="3hjh7kzpbkyf4">
                    "
                  </div>
                  <blockquote className="text-xl md:text-2xl font-medium text-gray-900 leading-relaxed mb-6" data-id="element-815" data-mp="33mu92shpy073">
                    {university.landingContent.testimonial.quote}
                  </blockquote>
                  <div className="text-sm text-gray-600" data-id="element-816" data-mp="6etawb091o6">
                    <span className="font-bold text-gray-900" data-id="element-817" data-mp="3rjhx53ds8arr">
                      {university.landingContent.testimonial.name}
                    </span>
                    {' — '}
                    {university.landingContent.testimonial.program}
                  </div>
                </div>
              </section>}

            {/* Career section with image */}
            {university.landingContent.careerSection && <section className="bg-white py-16 md:py-24 px-4" data-id="element-818" data-mp="10rzo5jus6i44">
                <div className="max-w-6xl mx-auto rounded-2xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-819" data-mp="vrk8gln10yiz">
                  <div className="grid grid-cols-1 md:grid-cols-2 items-stretch" data-id="element-820" data-mp="qu1xyqtd2b8v">
                    <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-821" data-mp="mrx53uaqlina">
                      <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5" data-id="element-822" data-mp="1jhoawpohas8e">
                        {university.landingContent.careerSection.headline}
                      </h2>
                      <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6" data-id="element-823" data-mp="19rmos3forjw4">
                        {university.landingContent.careerSection.body}
                      </p>
                      <button onClick={() => {
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }} className="self-start px-7 py-3 text-white font-bold rounded-lg transition-all shadow-md hover:shadow-lg" style={{
                  backgroundColor: university.ctaColor
                }} data-id="element-824" data-mp="2285ti1flk7g1">
                        Get Started
                      </button>
                    </div>
                    <div className="min-h-[280px] md:min-h-0" data-id="element-825" data-mp="2ygpvgeuf44r">
                      <img src={university.landingContent.careerSection.imageUrl} alt={university.landingContent.careerSection.imageAlt || university.name} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-826" data-mp="2vuh5dg7uiky" />
                    </div>
                  </div>
                </div>
              </section>}

            {/* Final CTA band */}
            {university.landingContent.finalCta && <section className="py-16 md:py-20 px-4" style={{
          backgroundColor: university.primaryColor
        }} data-id="element-827" data-mp="2hscllj732kmb">
                <div className="max-w-4xl mx-auto text-center text-white" data-id="element-828" data-mp="2rp7n8aynl14t">
                  <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-4" data-id="element-829" data-mp="35gm5gfkqkyat">
                    {university.landingContent.finalCta.headline}
                  </h2>
                  <p className="text-base md:text-lg text-white/85 leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-830" data-mp="31hn04tv3eziu">
                    {university.landingContent.finalCta.body}
                  </p>
                  <button onClick={() => {
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }} className="px-8 py-4 font-bold rounded-lg text-base md:text-lg transition-all shadow-lg hover:shadow-xl" style={{
              backgroundColor: university.ctaColor,
              color: '#fff'
            }} data-id="element-831" data-mp="3adw4mmxxgobu">
                    {university.landingContent.finalCta.buttonLabel || 'Request Information'}
                  </button>
                </div>
              </section>}
          </>}

        {/* Branded footer */}
        <footer className="bg-gray-900 text-gray-400 py-8 px-4 text-center text-xs" data-id="element-832" data-mp="3dzhdmxlz221m">
          <div className="max-w-5xl mx-auto space-y-2" data-id="element-833" data-mp="2z1xcy2w3yt19">
            <p data-id="element-834" data-mp="33zqegimguyv7">
              © {new Date().getFullYear()} {university.name}. All rights
              reserved.
            </p>
            <p className="text-gray-500" data-id="element-835" data-mp="x90pfgl0hy09">
              {university.accreditation} • Founded {university.founded}
            </p>
          </div>
        </footer>
      </main>
    </div>;
}